// client/src/pages/labEngine/notebookSimulator.jsx

import React, { useState, useEffect, useContext, useCallback, useRef,
  forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import { Plus, Download } from 'lucide-react';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import BackButton from '../../components/customs/backButton';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import NotebookCell from '../../components/labEngine/NotebookCell';
import { PyodideProvider, usePyodide } from '../../components/labEngine/PyodideContext';
import { NodeJSProvider, useNodeJS }   from '../../components/labEngine/NodeJSContext';
import { CourseDomains }               from '../../types/courseDomains';
import SimulationReport from '../../modals/labEngine/simulationReport';  // reuse existing


import { GlobalContext } from '../../context/provider';
import { useVisualCircuit, useSimulator, useComponentLibrary } from '../../hooks';
import useCourse from '../../hooks/useCourse';

import '../../styles/globalstyles.css';
import './notebookSimulator.css';

// ── Helpers (outside component to avoid recreation) ───────────────────────
const generateId = () => `cell_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const DEFAULT_CELLS = [
  {
    id: generateId(),
    code: `# Welcome to the AI Lab\nimport numpy as np\nimport pandas as pd\nprint("NumPy:", np.__version__)\nprint("Pandas:", pd.__version__)`,
    simOutputs: [], tag: null, label: 'Welcome', componentId: '',
  },
  {
    id: generateId(),
    code: `# Matplotlib example\nimport matplotlib.pyplot as plt\nimport numpy as np\n\nx = np.linspace(0, 2 * np.pi, 100)\nplt.figure(figsize=(7, 3))\nplt.plot(x, np.sin(x), label='sin(x)', color='#3b82f6')\nplt.plot(x, np.cos(x), label='cos(x)', color='#f59e0b')\nplt.legend()\nplt.title('Sine and Cosine')\nplt.tight_layout()\nplt.show()`,
    simOutputs: [], tag: null, label: 'Plot Example', componentId: '',
  },
];

// ── Inner notebook component (needs PyodideContext) ───────────────────────
const NotebookInner = forwardRef (({
  notebookTitle = 'Untitled Notebook',
  initialCells,
  externalLoading,
  emptyMessage,
  executionContext = 'pyodide',
  onSaveCell,
  onRegenerateCell,
  onMove,
  onTranslateCell
}, ref) => {

  const cellEditorRefs = useRef({});

  const [cells, setCells] = useState(() => {
    if (initialCells && initialCells.length > 0) {
      return initialCells.map(c => ({
        id:          c.cellId || generateId(),
        code:        c.code   || c.source || '',
        simOutputs:  c.simOutputs  || [],
        tag:         c.tag         || null,
        label:       c.label       || '',
        category:    c.category    || '',
        componentId: c.componentId || '',
      }));
    }
    return DEFAULT_CELLS;
  });

  // const { isLoading, error } = usePyodide();
  const pyodideCtx = usePyodide();
  const nodejsCtx  = useNodeJS();
  const activeCtx  = executionContext === 'nodejs' ? nodejsCtx : pyodideCtx;
  const { isLoading, error } = activeCtx || {};
  const [title, setTitle] = useState(notebookTitle);

  // ── Expose cell codes globally for pin validation in NotebookCell ─────────
  useEffect(() => {
    window.__notebookCellCodes = cells.map(c => ({ id: c.id, code: c.code }));
  }, [cells]);

  useImperativeHandle(ref, () => ({
    updateCellCode: (cellId, newCode) => {
      setCells(prev => prev.map(c =>
        c.id === cellId ? { ...c, code: newCode } : c
      ));
      const editor = cellEditorRefs.current[cellId];
      if (editor) editor.getModel()?.setValue(newCode);
    }
  }));

  const addCell = useCallback((afterId) => {
    const newCell = { id: generateId(), code: '', simOutputs: [], tag: null, label: '', componentId: '' };
    setCells(prev => {
      if (!afterId) return [...prev, newCell];
      const idx = prev.findIndex(c => c.id === afterId);
      const next = [...prev];
      next.splice(idx + 1, 0, newCell);
      return next;
    });
  }, []);

  const deleteCell = useCallback((id) => {
    setCells(prev => prev.length === 1 ? prev : prev.filter(c => c.id !== id));
  }, []);

  const updateCode = useCallback((id, code) => {
    setCells(prev => prev.map(c => c.id === id ? { ...c, code } : c));
  }, []);

  const moveCell = useCallback((id, direction) => {
    setCells(prev => {
      const idx = prev.findIndex(c => c.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      const next = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      onMove?.(next.map(c => c.id));
      return next;
    });
  }, [onMove]);

  const downloadNotebook = () => {
    const ipynb = {
      nbformat: 4, nbformat_minor: 5,
      metadata: {
        kernelspec: { display_name: 'Python 3', language: 'python', name: 'python3' },
        language_info: { name: 'python', version: '3.11' },
      },
      cells: cells.map(c => ({
        cell_type: 'code',
        source: c.code.split('\n'),
        metadata: {}, outputs: [], execution_count: null,
      })),
    };
    const blob = new Blob([JSON.stringify(ipynb, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.ipynb`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Inner notebook component (needs PyodideContext) ───────────────────────
  // Replace the existing NotebookInner return block with this.
  // All inline styles have been moved to aiNotebookPage.css additions.

  if (isLoading) {
    return (
      <div className="nb-pyodide-loading">
        <div className="nb-pyodide-spinner" />
        <div className="nb-pyodide-loading-text">
          <p>Starting Python environment</p>
          <p>🤖 Prof-iX is generating the code …</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nb-pyodide-error">
        <strong>Failed to load Python environment:</strong> {error}
      </div>
    );
  }

  if (!isLoading && !externalLoading && cells.length === 0 && emptyMessage) {
    return (
      <div className="nb-empty-state">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="nb-inner-wrap">

      {/* Header */}
      <div className="nb-header">
        <div className="nb-header-left">
          <div className="nb-header-logo">ProfiX</div>
          <span className="nb-header-kernel-badge">● Python 3.11</span>
        </div>
        <button onClick={downloadNotebook} className="nb-header-download-btn">
          <Download size={14} /> Download .ipynb
        </button>
      </div>

      {/* Cells */}
      {cells.map((cell, index) => (
        <NotebookCell
          key={cell.id}
          id={cell.id}
          index={index}
          category={cell.category || ''}
          executionContext={executionContext}
          initialCode={cell.code}
          simOutputs={cell.simOutputs || []}
          tag={cell.tag || null}
          label={cell.label || ''}
          componentId={cell.componentId || ''}
          savedCode={cell.code}
          onSaveCell={onSaveCell}
          onRegenerateCell={onRegenerateCell}
          onDelete={deleteCell}
          onInsertBelow={addCell}
          onCodeChange={updateCode}
          onMove={moveCell}
          isLast={index === cells.length - 1}
          onEditorMount={(editor) => {
            cellEditorRefs.current[cell.id] = editor;
          }}
          onTranslateCell={onTranslateCell}
        />
      ))}

      {/* Add cell */}
      <button onClick={() => addCell()} className="nb-add-cell-btn">
        <Plus size={14} /> Add cell
      </button>

      <p className="nb-footer-hint">
        Shift+Enter to run a cell · Variables persist across cells in this session
      </p>

    </div>
  );

});

// ── Outside and above NotebookSimulator ──────────────────────────────────────
// const NotebookProvider = ({ domain, children, executeNotebookCell }) => {
//   if (domain === 'AgenticAI') {
//     return (
//       <NodeJSProvider executeNotebookCell={executeNotebookCell}>
//         {children}
//       </NodeJSProvider>
//     );
//   }
//   return <PyodideProvider>{children}</PyodideProvider>;
// };
const NotebookProvider = ({ executionContext, children, executeNotebookCell }) => {
  if (executionContext === 'nodejs') {
    return (
      <NodeJSProvider executeNotebookCell={executeNotebookCell}>
        {children}
      </NodeJSProvider>
    );
  }
  return <PyodideProvider>{children}</PyodideProvider>;
};

// ── Main page component ───────────────────────────────────────────────────
const NotebookSimulator = () => {

  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate  = useNavigate();

  const cellEditorRefs = useRef({});
  const notebookRef = useRef(null);
  
  // ── State ─────────────────────────────────────────────────────────────────
  const [circuit,       setCircuit]       = useState(null);
  const [simulator,     setSimulator]     = useState(null);
  const [notebookCells, setNotebookCells] = useState([]);
  const [courseInfo,    setCourseInfo]    = useState(null);
  const [isGeneratingCells, setIsGeneratingCells] = useState(false);
  const [isLoading,         setIsLoading]         = useState(true);
  const [showSimulationReport, setShowSimulationReport] = useState(false);
  const [cellOutputs, setCellOutputs] = useState([]);  // collected from NotebookCell runs
  
  const executionContext = CourseDomains[simulator?.domain]?.executionContext || 'pyodide';
  // ── Hooks ─────────────────────────────────────────────────────────────────
  const {
    getCircuitBySessionId,
    updateNotebookOrder,
    error: circuitError,
    clearError: clearCircuitError,
  } = useVisualCircuit();

  const {
    getSimulatorBySessionId,
    generateNotebookCells,
    translateNotebookCells,
    executeNotebookCell,
    saveNotebookCells,
    saveGradingPayload, 
    submitGrades,  
    resetSimulator,
  } = useSimulator();

  const { getComponentById } = useComponentLibrary();
  const { getCourseById }    = useCourse();

  const { authState: { authData } } = useContext(GlobalContext);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!authData?.id) return;

        if (!sessionId) {
          console.error('❌ No sessionId in URL');
          alert('⚠️ Invalid notebook link. SessionId is required.');
          return;
        }

        // 1. Load course info
        if (courseId) {
          const course = await getCourseById(courseId);
          if (course.success) setCourseInfo(course.data);
        }

        // 2. Load simulator by sessionId
        const simResponse = await getSimulatorBySessionId(sessionId);
        if (!simResponse.success) {
          console.error('❌ Simulator not found for sessionId:', sessionId);
          alert('⚠️ Simulator not found. Please sync your circuit first.');
          navigate(-1);
          return;
        }
        setSimulator(simResponse.data);
        console.log('✅ Simulator loaded:', simResponse.data.simulatorId);

        // 3. Load circuit by sessionId
        const circuitResponse = await getCircuitBySessionId(sessionId);
        console.log('🔍 Circuit response:', circuitResponse);
        if (!circuitResponse.success) {
          console.error('❌ Failed to load circuit:', circuitResponse.error);
          alert(`⚠️ Failed to load circuit: ${circuitResponse.error || 'Unknown error'}`);
          return;
        }

        // 4. Enrich each component with componentLibraryData
        const enrichedCircuit = { ...circuitResponse.data };
        enrichedCircuit.components = await Promise.all(
          circuitResponse.data.components.map(async (comp) => {
            if (comp.componentId) {
              try {
                const libResponse = await getComponentById(comp.componentId);
                if (libResponse?.success) {
                  return { ...comp, componentLibraryData: libResponse.data };
                }
              } catch (err) {
                console.error('Error fetching component library data:', err);
              }
            }
            return comp;
          })
        );
        setCircuit(enrichedCircuit);

        // 5. Load or generate notebook cells
        setIsGeneratingCells(true);
        showNotification('🤖 Prof-iX is loading your notebook...', 'info');

        try {
          let cells;
          const savedCells = simResponse.data.notebookCells || [];
          const circuitInstanceIds = enrichedCircuit.components.map(c => `cell_${c.instanceId}`);

          // const allMatch = savedCells.length > 0 &&
          //   circuitInstanceIds.length === savedCells.length &&
          //   circuitInstanceIds.every(expectedId =>
          //     savedCells.some(saved => saved.cellId === expectedId)
          //   );
          const circuitCellIds = new Set(circuitInstanceIds);
          const allMatch = savedCells.length > 0 &&
            circuitInstanceIds.every(expectedId =>
              savedCells.some(saved => saved.cellId === expectedId)
            );

          if (allMatch) {
            console.log('📂 Loading notebook cells from simulator record...');
            cells = savedCells;
            showNotification('Notebook loaded!', 'success');
          } else {
            console.log('🔍 Cell mismatch details:', {
              savedCount:   savedCells.length,
              circuitCount: circuitInstanceIds.length,
              missingIds:   circuitInstanceIds.filter(id =>
                !savedCells.some(saved => saved.cellId === id)
              )
            });
            showNotification('🤖 Prof-iX is generating your notebook...', 'info');
            cells = await generatePrimaryNotebookCellsWithAI(enrichedCircuit, simResponse.data);
            if (cells.length > 0) {
              await saveNotebookCells(simResponse.data._id, cells);
              console.log('💾 Fresh notebook cells saved to simulator');
            }
            showNotification('Notebook generated!', 'success');
          }

          // Sort by notebookOrder if present before setting cells
          const sortedCells = [...cells].sort((a, b) => {
            const aOrder = enrichedCircuit.components.find(
              c => `cell_${c.instanceId}` === a.cellId
            )?.notebookOrder ?? Infinity;
            const bOrder = enrichedCircuit.components.find(
              c => `cell_${c.instanceId}` === b.cellId
            )?.notebookOrder ?? Infinity;
            return aOrder - bOrder;
          });
          setNotebookCells(sortedCells);

        } catch (err) {
          console.error('Error generating/loading cells:', err);
          showNotification('Failed to load notebook cells', 'error');
        } finally {
          setIsGeneratingCells(false);
        }

      } catch (err) {
        console.error('❌ Error loading AI notebook page:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionId, courseId, authData?.id]);

  // ── generatePrimaryNotebookCellsWithAI — mirrors generatePrimaryCodeWithAI ──
  const generatePrimaryNotebookCellsWithAI = async (circuit, simulator) => {
    try {
      console.log('🔍 Starting notebook cell generation...');

      const componentIds = circuit.components
        .map(comp => comp.componentId)
        .filter(Boolean);

      if (componentIds.length === 0) {
        console.warn('⚠️ No components in circuit');
        return [];
      }

      console.log('🔍 Component IDs for generation:', componentIds);

      const response = await generateNotebookCells(
        componentIds,
        circuit.components,
        simulator?.domain
      );

      if (response.success) {
        console.log(`✅ Generated ${response.data.componentCount} notebook cells`);
        return response.data.cells;
      } else {
        console.error('❌ Cell generation failed:', response.error);
        return [];
      }

    } catch (error) {
      console.error('❌ Error generating notebook cells:', error);
      return [];
    }
  };

  const handlePostToAISession = async () => {
    const isGradedLab = sessionId && sessionId.includes('_LAB');
    if (!isGradedLab || !simulator) return;

    try {
      const duration = simulator?.state?.uptime || 0;

      // Collect AI evidence from notebookCells — matches CourseDomains.AI.gradingPayloadFields
      const cellOutputsFromCells = notebookCells.map(c => ({
        cellId:      c.cellId,
        componentId: c.componentId,
        label:       c.label || '',
        category:    c.category || '',
        executed:    !!(c.simOutputs?.stdout || c.simOutputs?.hasPlot),
        stdout:      c.simOutputs?.stdout  || '',
        error:       c.simOutputs?.error   || '',
        hasPlot:     c.simOutputs?.hasPlot || false,
      }));

      const gradingData = {
        cellOutputs:   cellOutputsFromCells,
        cellsExecuted: cellOutputsFromCells.filter(c => c.executed).length,
        totalCells:    notebookCells.length,
        hasErrors:     cellOutputsFromCells.some(c => !!c.error),
        stdoutSummary: cellOutputsFromCells.map(c => c.stdout).join('\n').slice(0, 2000),
      };

      const response = await saveGradingPayload(simulator._id, duration, gradingData);

      if (response.success) {
        setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
        showNotification('Notebook submitted for grading!', 'success');
      } else {
        showNotification(`Failed to submit: ${response.error}`, 'error');
      }
    } catch (err) {
      showNotification('Failed to submit', 'error');
    }
  };

  // const handleGrade = async () => {
  //   await handlePostToAISession();  // saves payload with status='pending'
  //   setShowSimulationReport(true);  // then opens modal
  // };
  const handleGrade = async () => {
    // Optionally reset execution state before regrade
    if (simulator?._id && simulator?.gradingPayload) {
      await resetSimulator(simulator._id);
    }

    let freshSimulator = simulator;
    const res = await getSimulatorBySessionId(sessionId);
    if (res.success) {
      freshSimulator = res.data;
      setSimulator(res.data);
    }
    await handlePostToAISession();
    setShowSimulationReport(true);
  };

  const handleSubmitGrades = async () => {
    console.log('🔍 submitGrades — simulator._id:', simulator?._id, 'gradingPayload:', simulator?.gradingPayload);
    if (!simulator?._id) return;

    const response = await submitGrades(simulator._id);
    if (response.success) {
      setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
      showNotification('Grades submitted!', 'success');
    } else {
      showNotification(`Failed to submit grades: ${response.error}`, 'error');
    }
  };

  // ── Header ────────────────────────────────────────────────────────────────
  const profileHeaderData = {
    ...authData,
    // title: '🧠 AI Notebook',
    // subTitle: circuit?.name || 'AI Notebook Simulator',
  };

  // const NotebookProvider = ({ domain, children, executeNotebookCell }) => {
  //   if (domain === 'AgenticAI') {
  //     return (
  //       <NodeJSProvider executeNotebookCell={executeNotebookCell}>
  //         {children}
  //       </NodeJSProvider>
  //     );
  //   }
  //   return <PyodideProvider>{children}</PyodideProvider>;
  // };

  // ── Render ────────────────────────────────────────────────────────────────
  
  return (
    <div className="ai-notebook-page">

      <div className="header-row">
          <ProfileHeader 
              data={profileHeaderData} 
              activePage="simulator" 
          />
          <div className="headerMiddle">
          <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={5} icons={[]} />
      </div>

      {/* Toolbar */}
      <div className="ai-notebook-toolbar">
        <h3>🧠 AI Notebook</h3>
        <div className="ai-notebook-toolbar-left">
          <BackButton 
              to="-1"
              activeTab=""
              label="←"
              bgColor="#6b7280"
          />
          {sessionId && sessionId.includes('_LAB') && (
            <>
              {/* Show Report — only when gradingPayload exists */}
              {simulator?.gradingPayload && (
                <CustomButton
                  buttonTitle="📊 Show Report"
                  action={() => setShowSimulationReport(true)}
                  bgColor="#8b5cf6"
                  disabled={!simulator}
                />
              )}

              {/* Grade — always shown, disabled only when graded */}
              <CustomButton
                buttonTitle="📤 Grade"
                action={handleGrade}
                bgColor="#8b5cf6"
                disabled={
                  !simulator ||
                  simulator?.gradingPayload?.status === 'graded'
                }
              />
            </>
          )}
          {/* {circuit && ( */}
          <span className="ai-notebook-flow-name">
            {circuit && circuit.labId && (
              <span className="ai-notebook-lab-tag">📚 {circuit.labId}</span>
            )}
            {circuit && circuit.name && (<span>🧠 {circuit.name}</span>)}
            <span className="ai-notebook-session-id">Session: {sessionId}</span>
          </span>
          {/* )} */}
        </div>
        {/* <div className="ai-notebook-toolbar-right">
        </div> */}
      </div>

      {/* Main content — PyodideProvider wraps NotebookInner */}
      <NotebookProvider
      // <PyodideProvider
        // domain={simulator?.domain}
        executionContext={executionContext}
        executeNotebookCell={executeNotebookCell}
      >
        <div className="ai-notebook-content">
          {isLoading ? (
            <div className="loading-overlay">
              <div className="loader" />
              {/* <p>Loading AI notebook...</p> */}
            </div>
          ) : (
            <NotebookInner
              ref={notebookRef}
              initialCells={notebookCells}
              domain={simulator?.domain}
              notebookTitle={circuit?.name || 'AI Notebook'}
              externalLoading={isGeneratingCells}
              emptyMessage="No cells generated — go back to the canvas and sync your circuit"
              executionContext={executionContext} 
              onMove={async (orderedCellIds) => {
                if (!circuit?._id) return;
                const orderUpdates = orderedCellIds.map((cellId, index) => ({
                  instanceId: cellId.replace('cell_', ''),
                  notebookOrder: index,
                }));
                await updateNotebookOrder(circuit._id, orderUpdates);
              }}
              onSaveCell={(cellId, newCode, output) => {
                if (!simulator) return;
                setNotebookCells(prev => {
                  const updatedCells = prev.map(c =>
                    c.cellId === cellId ? {
                      ...c,
                      code: newCode,
                      updatedAt: new Date(),
                      ...(output ? {
                        simOutputs: {
                          stdout:  output.stdout || '',
                          error:   output.error  || '',
                          hasPlot: !!output.plot,
                          ranAt:   new Date()
                        },
                        executionCount: (c.executionCount || 0) + 1
                      } : {})
                    } : c
                  );
                  saveNotebookCells(simulator._id, updatedCells);
                  return updatedCells;
                });
              }}
              onRegenerateCell={async (cellId) => {
                // console.log('🔄 Regenerate clicked for cellId:', cellId);
                // console.log('🔄 circuit.components:', circuit?.components?.map(c => ({
                //   instanceId: c.instanceId,
                //   cellKey: `cell_${c.instanceId}`
                // })));

                if (!circuit || !simulator) return;
                const comp = circuit.components.find(
                  c => `cell_${c.instanceId}` === cellId
                );
                // console.log('🔄 Found comp:', comp?.componentId, comp?.label);

                if (!comp) {
                  console.error('❌ No component found for cellId:', cellId);
                  return;
                }
                
                const response = await generateNotebookCells([comp.componentId], [comp]);
                if (response.success && response.data.cells.length > 0) {
                  const newCode = response.data.cells[0].code;

                  // Update editor first
                  notebookRef.current?.updateCellCode(cellId, newCode);

                  // Then update state and save — use newCode not stale notebookCells
                  const updatedCells = notebookCells.map(c =>
                    c.cellId === cellId ? { ...c, code: newCode, updatedAt: new Date() } : c
                  );
                  setNotebookCells(updatedCells);
                  await saveNotebookCells(simulator._id, updatedCells);
                  showNotification('Notebook cell has been regenerated!', 'success');
                }
              }}
              onCellOutput={(cellId, componentId, output) => {
                // Update grading payload state
                setCellOutputs(prev => {
                  const existing = prev.findIndex(c => c.cellId === cellId);
                  const entry = { cellId, componentId, executed: true, stdout: output.stdout, error: output.error };
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = entry;
                    return updated;
                  }
                  return [...prev, entry];
                });

                // Use functional update to always read latest notebookCells
                setNotebookCells(prev => {
                  const updatedCells = prev.map(c =>
                    c.cellId === cellId ? {
                      ...c,
                      simOutputs: {
                        stdout:  output.stdout || '',
                        error:   output.error  || '',
                        hasPlot: !!output.plot,
                        ranAt:   new Date()
                      },
                      executionCount: (c.executionCount || 0) + 1,
                      updatedAt: new Date()
                    } : c
                  );
                  // Save to DB inside the functional update — always has latest state
                  if (simulator?._id) {
                    saveNotebookCells(simulator._id, updatedCells);
                  }
                  return updatedCells;
                });
              }}
              onEditorMount={(cellId, editor) => {
                cellEditorRefs.current[cellId] = editor;
              }}
              onTranslateCell={translateNotebookCells}
            />
          )}
        </div>
      {/* </PyodideProvider> */}
      </NotebookProvider>

      {/* Generating overlay */}
      {isGeneratingCells && (
        <div className="ai-loading-overlay">
          <div className="ai-loading-spinner" />
          <p className="ai-loading-title">🤖 Prof-iX is loading your notebook...</p>
          <p className="ai-loading-subtitle">Building cells from your pipeline</p>
        </div>
      )}

      <ProfIXChatbot
        userName={authData?.name}
        courseId={courseId}
        courseName={courseInfo?.title}
        category={courseInfo?.category}
      />

      <SimulationReport
        visible={showSimulationReport}
        simulator={simulator}
        circuit={circuit}
        domain="AI"
        onClose={() => setShowSimulationReport(false)}
        // onSubmit={handlePostToAISession}
        onSubmit={handleSubmitGrades}
        onRefreshSimulator={async () => {
          const res = await getSimulatorBySessionId(sessionId);
          if (res.success) setSimulator(res.data);
        }}
      />

      {circuitError && (
        <div className="circuit-error-container">
          ❌ {circuitError}
          <button onClick={clearCircuitError} className="circuit-error-close">✖</button>
        </div>
      )}

    </div>
  );
};

export default NotebookSimulator;